import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { FilterRuleBuilder } from './FilterRuleBuilder';
import { AdjustmentSettings, FilterRule } from '@/types/adjustment-filters';
import { Separator } from '@/components/ui/separator';

interface AdjustmentSettingsPanelProps {
  settings: AdjustmentSettings;
  onChange: (settings: AdjustmentSettings) => void;
}

export const AdjustmentSettingsPanel: React.FC<AdjustmentSettingsPanelProps> = ({ 
  settings, 
  onChange 
}) => {
  const [enableMarkupRemoval, setEnableMarkupRemoval] = useState((settings?.filters?.length || 0) > 0);
  const [enableDiscount, setEnableDiscount] = useState(!!(settings?.discountPercentage));

  const handleFiltersChange = (filters: FilterRule[]) => {
    onChange({ ...settings, filters });
  };

  const handleDiscountChange = (percentage: number) => {
    onChange({ 
      ...settings, 
      discountPercentage: percentage,
      discountType: 'percentage'
    });
  };

  // Sync toggle state when settings prop changes (e.g., loaded from database)
  useEffect(() => {
    setEnableMarkupRemoval((settings?.filters?.length || 0) > 0);
    setEnableDiscount(!!(settings?.discountPercentage));
  }, [settings?.filters?.length, settings?.discountPercentage]);

  useEffect(() => {
    if (!enableMarkupRemoval && settings.filters.length > 0) {
      onChange({ ...settings, filters: [] });
    }
  }, [enableMarkupRemoval]);

  useEffect(() => {
    if (!enableDiscount && settings.discountPercentage) {
      onChange({ ...settings, discountPercentage: undefined });
    }
  }, [enableDiscount]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cost Analysis & Adjustments</CardTitle>
        <CardDescription>
          Configure price adjustments for this estimate without modifying source line items
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Markup Removal Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="markup-removal" className="text-base">
                Remove Markups
              </Label>
              <p className="text-sm text-muted-foreground">
                Remove markups from specific line items based on filters
              </p>
            </div>
            <Switch
              id="markup-removal"
              checked={enableMarkupRemoval}
              onCheckedChange={setEnableMarkupRemoval}
            />
          </div>

          {enableMarkupRemoval && (
            <div className="ml-0 space-y-2">
              <Label>Filter Conditions</Label>
              <p className="text-sm text-muted-foreground">
                Define which line items should have markups removed
              </p>
              <FilterRuleBuilder 
                rules={settings.filters || []} 
                onChange={handleFiltersChange} 
              />
            </div>
          )}
        </div>

        <Separator />

        {/* Discount Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="discount" className="text-base">
                Apply Discount
              </Label>
              <p className="text-sm text-muted-foreground">
                Apply a percentage discount to the adjusted total
              </p>
            </div>
            <Switch
              id="discount"
              checked={enableDiscount}
              onCheckedChange={setEnableDiscount}
            />
          </div>

          {enableDiscount && (
            <div className="ml-0 space-y-2">
              <Label htmlFor="discount-percentage">Discount Percentage (%)</Label>
              <Input
                id="discount-percentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.discountPercentage ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') handleDiscountChange(0);
                  else { const num = parseFloat(val); if (!isNaN(num)) handleDiscountChange(num); }
                }}
                className="w-[200px]"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
